import { useEffect, useState } from 'react';
import type { ApiClient } from '../api';
import type { UsvBemessung, UsvDosen, UsvItVerteiler, UsvStand, UsvVorgabe } from './elektro/usv';
import { kilo, USV_BEDARF_HINWEIS } from './elektro/usv';
import { PDF_HINWEIS } from './elektro/sicherheitsstrom';
import { zerlegeBefund } from './elektro/sicherheitstechnik';
import {
  ContentSection, DataTable, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../muster';

const zahl = (w: number | null | undefined, stellen = 0) =>
  w === null || w === undefined
    ? '—'
    : w.toLocaleString('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: stellen });

/**
 * Reiter „USV" der Seite Sicherheitsstrom (W2-A1, 16.10.2026).
 *
 * LIEST `GET …/elektro/usv`: Bedarfsbemessung der USV je Verbrauchergruppe (IT-Verteiler,
 * USV-Steckdosen), Batterie, Ladung, Eingangsleistung für die Leistungsbilanz, Batterieraum, dazu die
 * IT-Verteiler mit einer dezentralen Vergleichsbemessung und was im Modell fehlt. Gerechnet wird auf dem
 * Server, die Seite schreibt nichts. PDF ELT-USV (neue Fassung nur bei geändertem Stand, F11) und Excel-Mappe.
 */
export function UsvAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<UsvStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api.elektroUsv(projektId)
      .then((s) => { if (aktiv) { setStand(s ?? null); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  async function laden(art: 'nachweis' | 'excel') {
    setLaeuft(art);
    try {
      if (art === 'nachweis') await api.elektroUsvNachweisLaden(projektId);
      else await api.elektroUsvListen(projektId);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  return (
    <>
      <div className="aktionen">
        <button type="button" className="m-knopf" disabled={laeuft !== null}
                onClick={() => { void laden('excel'); }}>
          USV-Excel-Mappe
        </button>
        <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
                onClick={() => { void laden('nachweis'); }}>
          USV-Bemessung als PDF
        </button>
      </div>
      {fehler && <ErrorState titel="Die USV-Bemessung konnte nicht geladen werden." technisch={fehler} />}
      {!fehler && !stand && <LoadingState />}
      {stand && <UsvInhalt stand={stand} />}
    </>
  );
}

function UsvInhalt({ stand }: { stand: UsvStand }) {
  const k = stand.kennzahlen;
  const g = stand.gesamt;

  const bemessung: Spalte<UsvBemessung>[] = [
    { schluessel: 'gruppe', titel: 'Gruppe', wert: (z) => z.gruppe },
    { schluessel: 'pb', titel: 'P_B', einheit: 'W', art: 'zahl', nachkomma: 0, wert: (z) => z.lastBetriebW },
    { schluessel: 'sa', titel: 'S_A', einheit: 'VA', art: 'zahl', nachkomma: 0, wert: (z) => z.scheinleistungVa },
    { schluessel: 'cmin', titel: 'C_min', einheit: 'Ah', art: 'zahl', nachkomma: 1, wert: (z) => z.mindestbedarfAh },
    { schluessel: 'cerf', titel: 'C_erf (C10)', einheit: 'Ah', art: 'zahl', nachkomma: 1, wert: (z) => z.kapazitaetErforderlichAh },
    { schluessel: 'e', titel: 'Energie', einheit: 'kWh', art: 'zahl', nachkomma: 2, wert: (z) => z.energieWh / 1000 },
    { schluessel: 'pein', titel: 'P_ein', einheit: 'W', art: 'zahl', nachkomma: 0, wert: (z) => z.eingangsleistungW },
    { schluessel: 'q', titel: 'Lüftung Q', einheit: 'm³/h', art: 'zahl', nachkomma: 2, wert: (z) => z.lueftungM3h },
  ];

  const it: Spalte<UsvItVerteiler>[] = [
    { schluessel: 'k', titel: 'Verteiler', wert: (z) => z.kennzeichen },
    { schluessel: 'r', titel: 'Raum', wert: (z) => z.raumnummer },
    { schluessel: 'v', titel: 'Versorgung', wert: (z) => z.versorgungsart },
    { schluessel: 'e', titel: 'gespeist aus', wert: (z) => z.einspeisung },
    { schluessel: 'p', titel: 'P_B', einheit: 'W', art: 'zahl', nachkomma: 0, wert: (z) => z.bedarfsleistungW },
    { schluessel: 's', titel: 'S_A dezentral', einheit: 'VA', art: 'zahl', nachkomma: 0, wert: (z) => z.dezentral?.scheinleistungVa ?? null },
    { schluessel: 'c', titel: 'C_erf dezentral', einheit: 'Ah', art: 'zahl', nachkomma: 1, wert: (z) => z.dezentral?.kapazitaetErforderlichAh ?? null },
  ];

  const dosen: Spalte<UsvDosen>[] = [
    { schluessel: 'v', titel: 'Verteiler', wert: (z) => z.verteiler },
    { schluessel: 'a', titel: 'Versorgung', wert: (z) => z.versorgungsart },
    { schluessel: 'k', titel: 'Kreise', art: 'zahl', wert: (z) => z.kreise },
    { schluessel: 'd', titel: 'Dosen', art: 'zahl', wert: (z) => z.dosen },
  ];

  const vorgaben: Spalte<UsvVorgabe>[] = [
    { schluessel: 'f', titel: 'Vorgabe', wert: (z) => z.feld },
    { schluessel: 'w', titel: 'Wert', wert: (z) => `${z.wert} ${z.einheit ?? ''}`.trim() },
    { schluessel: 'h', titel: 'Herkunft', wert: (z) => z.herkunft + (z.rueckfall ? ' (Rückfall)' : '') },
    { schluessel: 'b', titel: 'bestätigt', wert: (z) => (z.bestaetigt ? 'ja' : 'nein') },
    { schluessel: 'n', titel: 'Normbezug', wert: (z) => z.normbezug },
  ];

  return (
    <>
      <p className="hinweis hinweis-warnung">{USV_BEDARF_HINWEIS}</p>
      <KpiReihe>
        <KpiCard titel="Bedarf P_B" wert={`${kilo(g.lastBetriebW)} kW`}
                 vergleich={`${k.itVerteiler} IT-Verteiler · ${k.usvSteckdosen} USV-Steckdosen`} />
        <KpiCard titel="USV S_A" wert={`${kilo(g.scheinleistungVa)} kVA`}
                 vergleich={`mit Reserve, Autonomie ${zahl(g.autonomieMin)} min`} />
        <KpiCard titel="Batterie C_erf" wert={`${zahl(g.kapazitaetErforderlichAh)} Ah`}
                 vergleich={`bei ${zahl(g.dcSpannungV)} V · ${zahl(g.energieWh / 1000, 1)} kWh`} />
        <KpiCard titel="Warnungen" wert={k.warnungen} vergleich={`${k.befunde} Befunde`} />
      </KpiReihe>

      <ContentSection titel="Befunde">
        <ul className="liste">
          {stand.befunde.map((b, i) => {
            const { stufe, text } = zerlegeBefund(b);
            const z: Zustand = stufe === 'fehler' ? 'fehler' : stufe === 'warnung' ? 'warnung' : 'info';
            return <li key={`${i}-${text.slice(0, 24)}`}><StatusBadge zustand={z} text={text} /></li>;
          })}
        </ul>
      </ContentSection>

      <ContentSection titel="Bemessung je Gruppe und gesamt">
        <DataTable name="Bemessung" spalten={bemessung} zeilen={[...stand.jeGruppe, g]} schluessel={(z) => z.gruppe}
                   suche={false} dicht />
        <p className="hinweis">{g.rechenweg.join(' · ')}</p>
      </ContentSection>

      <ContentSection titel="IT-Verteiler (zum Vergleich: je Verteiler eine dezentrale USV)">
        <DataTable name={`IT-Verteiler (${stand.itVerteiler.length})`} spalten={it} zeilen={stand.itVerteiler}
                   schluessel={(z) => z.kennzeichen} suche={false} dicht />
      </ContentSection>

      <ContentSection titel="USV-Steckdosen je Verteiler">
        <DataTable name={`Verteiler mit USV-Steckdosen (${stand.dosen.length})`} spalten={dosen} zeilen={stand.dosen}
                   schluessel={(z) => z.verteiler} suche={false} dicht />
      </ContentSection>

      <ContentSection titel="Vorgaben">
        <DataTable name="Vorgaben" spalten={vorgaben} zeilen={stand.vorgaben} schluessel={(z) => z.feld}
                   suche={false} dicht />
      </ContentSection>
    </>
  );
}
